import upperFirst from 'lodash/upperFirst';
import theme, { Size } from '../../../lib/theme';
import { branch } from '../../../lib/fpUtils';
import { SpacerProps, ResponsiveSize, StaticSize } from './Spacer';

const { colors, scale, scaleRaw, layouts, media } = theme;

type PropFn = (s: Side) => string;
type Side = 'Left' | 'Right' | 'Top' | 'Bottom';
type Sides = Side[];
type BuildPadding = (...args: SpacerProps['self'][]) => any;
type FluidSize = Size | number | string;

const { isArray } = Array;
const mediaMin = (px: number): string =>
  `@media screen and (min-width: ${px}px)`;

const tryScale = (v: Size | string | number) =>
  typeof v === 'string' ? scaleRaw[v] : v;

export const fluid = (
  ...properties: string[]
): ((
  min?: FluidSize,
  max?: FluidSize,
  minVW?: number,
  maxVW?: number
) => any) => (min = 16, max = 20, minVW = 320, maxVW = 2080): any =>
  properties.reduce(
    (acc, property) => ({
      ...acc,
      [property]: tryScale(min),
      [mediaMin(minVW)]: {
        ...acc[mediaMin(minVW)],
        [property]: `calc(${tryScale(min)}px + (${tryScale(max) -
          tryScale(min)}) * ((100vw - ${minVW}px) / (${maxVW - minVW})))`,
      },
      [mediaMin(maxVW)]: {
        ...acc[mediaMin(maxVW)],
        [property]: tryScale(max),
      },
    }),
    {}
  );

const cssName = (prop: string) => (s: Side) => `${prop}${upperFirst(s)}`;
const jsName = (prop: string) => (s: Side) => `${prop}${upperFirst(s)}`;
const staticProp = (propFn: PropFn) => (size: StaticSize, sides: Sides) =>
  sides.reduce(
    (acc: any, side) => ({
      ...acc,
      [propFn(side)]: scale[size as Size] || size,
    }),
    {}
  );

const responsiveProp = (propFn: PropFn) => (
  size: ResponsiveSize,
  sides: Sides
) => fluid(...sides.map(propFn))(...size);

const staticSpacing = staticProp(jsName('margin'));
const responsiveSpacing = responsiveProp(cssName('margin'));
const staticPadding = staticProp(jsName('padding'));
const responsivePadding = responsiveProp(cssName('padding'));
const padding = branch(isArray, responsivePadding, staticPadding);
const spacing = branch(isArray, responsiveSpacing, staticSpacing);

const buildSpacing = spacing;
const buildSidePadding: BuildPadding = (x, y, l?, r?, t?, b?) => ({
  ...padding(l || r || x, [!r && 'left', !l && 'right'].filter(Boolean)),
  ...padding(t || b || y, [!b && 'top', !t && 'bottom'].filter(Boolean)),
});
const buildAllPadding: BuildPadding = xy =>
  padding(xy, ['left', 'right', 'bottom', 'top']);

export default ({
  brand = 'sas',
  align,
  justify,
  reverse,
  row,
  split,
  space,
  self,
  selfH,
  selfV,
  selfT,
  selfR,
  selfB,
  selfL,
  width,
  maxWidth,
  relative,
  separator,
  fill,
  clip,
  clipX,
  clipY,
  flex,
}: SpacerProps) => ({
  container: {
    flex,
    position: relative ? 'relative' : null,
    ...layouts[row ? 'row' : 'column'],
    flexDirection: `${row ? 'row' : 'column'}${reverse ? '-reverse' : ''}`,
    ...(self
      ? buildAllPadding(self)
      : buildSidePadding(selfH, selfV, selfL, selfR, selfT, selfB)),
    justifyContent: justify || (split ? 'space-between' : 'flex-start'),
    alignItems: align || (row ? 'center' : 'stretch'),
    maxWidth: maxWidth ?? (width ? '100%' : layouts.contentPane),
    width: width ?? '100%',
    overflowX: clip || clipX ? 'hidden' : null,
    overflowY: clip || clipY ? 'hidden' : null,
    '& > *': {
      flex: fill ? '1 0' : '',
    },
    '& > * + *': {
      ...buildSpacing(
        space,
        row ? [reverse ? 'right' : 'left'] : [reverse ? 'bottom' : 'top']
      ),
      borderWidth: separator ? 0 : null,
      borderStyle: separator ? 'solid' : null,
      borderColor: separator
        ? colors[brand][separator] || colors[separator] || 'initial'
        : null,
      [row ? 'marginLeft' : 'marginTop']: scale[space as Size],
      [row ? 'borderLeftWidth' : 'borderTopWidth']: separator ? 1 : null,
    },
    [media.xl.down]: {
      maxWidth: '100% !important',
      width: width ?? '100% !important',
    }
  },
});
